/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { of } from 'rxjs';
import { FieldSet, LAYOUT_OPTIONS } from '@src/app/shared/form/field-set/field-set.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { FormConfig } from "@src/app/shared/form/FormConfig";
import { AbstractObject, TenantId, uuid } from '@src/app/model/abstract-object';
import { BCode } from '@src/app/model/bcode';
import { Schedule } from '@src/app/model/schedule';
import { Txn } from '@src/app/model/txn';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { Action } from '@src/app/model/action';
import { Memo } from '@src/app/model/memo';
import { Person } from '@src/app/model/person';

export class SupplierCategory extends AbstractObject {
    globalSupplierId: uuid;
    globalCategoryId: uuid;
    category: AbstractObject;
    constructor(o: Partial<Supplier> = {}) {
        super(o);
    }
}

export class SupplyCategory extends AbstractObject {
    constructor(o: Partial<Supplier> = {}) {
        super(o);
    }

    static pickDialogFormConfig(): FormConfig {
        return new FormConfig({
            fieldSet: new FieldSet({
                fields: [
                    FieldMaker.id(),
                    FieldMaker.rev(),
                    FormTextComponent.make('Name', 'name'),
                    FieldMaker.notes(),
                ],
                formLayout: LAYOUT_OPTIONS.singleCol,
            }),
            title: $localize`Supply Categories`,
            mode: 'new',
            objectFactory: () => of(new SupplyCategory()),
        });
    }
}

export class PreferredSupplier extends AbstractObject {
    name: string;
    notes: string;
    code: string;
    omcTeamId: TenantId;
    globalSupplierId: uuid;
    globalSupplier: Supplier;
    template: SupplierTemplate[] = [];
    transactions: Txn[] = [];
    txnWithPayments: Txn[] = [];
    actions: Action[] = [];
    memos: Memo[] = [];
    omcUsage: AbstractObject[] = [];
    contacts: Person[] = [];
    constructor(o: Partial<Supplier> = {}) {
        super(o);
    }
}

export class SupplierTemplate extends AbstractObject {
    bCodeId: uuid;
    bCode: BCode;
    scheduleId: uuid;
    schedule: Schedule;
    supplierId: uuid;

    constructor(o: Partial<Supplier> = {}) {
        super(o);
    }
}
export class Supplier extends AbstractObject {
    constructor(o: Partial<Supplier> = {}) {
        super(o);
    }
    name: string;
    address: string;
    email: string;
    emailAccounts: string;
    phone: string;
    web: string;
    notes: string;
    VAT: string;
    IBAN: string;
    DUNS: string;
    categories: SupplierCategory[] = [];
    /**
     * Budget code / schedule pairs this team has already used on purchases from this supplier.
     * Built by Supplier::getFullItem, so only the single-record read carries it - never a list -
     * and it is scoped to the team that asked, which is what _forceTeam decides.
     */
    template: SupplierTemplate[] = [];
    preferred: PreferredSupplier;
    omcTeamId: TenantId;
    contacts?: Person[] = [];

    /**
     * Columns for the supplier picker. Read through the plain suppliers endpoint with the OMC as
     * the forced team, which TeamScope narrows to the agency's own suppliers plus any private to
     * that OMC - not the same list as suppliers/preferred, which only offers ones already traded with.
     */
    static pickDialogFormConfig(): FormConfig {
        return new FormConfig({
            fieldSet: new FieldSet({
                fields: [
                    FormTextComponent.make('Name', 'name'),
                    FormTextComponent.make('Code', 'code'),
                    FormTextComponent.make('Phone', 'phone'),
                    FormTextComponent.make('Email', 'email'),
                ],
                formLayout: LAYOUT_OPTIONS.singleCol,
            }),
            title: $localize`Suppliers`,
            mode: 'list',
        });
    }

    static categoryList(o: Supplier) {
        let ret = '';
        for (const c of o.categories) {
            if (ret.length > 0) {
                ret += ', ';
            }
            ret += c.category.name;
        }
        return ret;
    }
}
